<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useAudienceStore } from '@/app/stores/audience'
import { localizedAudienceText } from '@/core/i18n/audienceText'
import { assertSafeArtifactUrl } from '@/core/release/verifiedFetch'
import { audienceEnvironment } from '@/core/runtime/environment'
import {
  broadcastReadStorageKey,
  readStoredBroadcastIds,
  reconcileBroadcastHistory,
  rememberStoredBroadcastId,
  revokeBroadcastState,
  type BroadcastPanelStatus,
} from '@/shared/ui/broadcastState'
import VfImagePreview from '@/shared/ui/VfImagePreview.vue'

interface BroadcastImage { url: string; width: number; height: number; sortOrder: number }
interface BroadcastMessage { id: string; seq: number; level: 'normal' | 'warning' | 'urgent'; title: string; body: string; time: string; expiresAt: string; images: BroadcastImage[] }
interface PreviewImage { image: BroadcastImage; messageId: string }

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ close: []; status: [status: BroadcastPanelStatus] }>()
const audience = useAudienceStore()
const messages = ref<BroadcastMessage[]>([])
const error = ref('')
const activeMessage = ref<BroadcastMessage | null>(null)
const emergencyMessage = ref<BroadcastMessage | null>(null)
const emergencyQueue = ref<BroadcastMessage[]>([])
const previewImage = ref<PreviewImage | null>(null)
const readRevision = ref(0)
const panel = ref<HTMLElement | null>(null)
const panelClose = ref<HTMLButtonElement | null>(null)
const detail = ref<HTMLElement | null>(null)
const detailClose = ref<HTMLButtonElement | null>(null)
const emergency = ref<HTMLElement | null>(null)
const emergencyDismiss = ref<HTMLButtonElement | null>(null)
const knownEmergencyIds = new Set<string>()
const revokedSeqById = new Map<string, number>()
let emergencySessionInitialized = false
let mounted = false
let source: EventSource | null = null
let pollTimer = 0
let lastSeq = 0
let panelReturnFocus: HTMLElement | null = null
let detailReturnFocus: HTMLElement | null = null
let emergencyReturnFocus: HTMLElement | null = null

const apiBase = audienceEnvironment.apiBase
const eventId = computed(() => audience.config?._runtime?.eventId || '')
const readStorageKey = computed(() => broadcastReadStorageKey(eventId.value, 'audience'))
const emergencyStorageKey = computed(() => `venue-h5-emergency-dismissed-v1:audience:${eventId.value}`)
const t = (value: string) => localizedAudienceText(audience.config, audience.locale, value)
const formatTime = (value: string) => { const date = new Date(value); return Number.isNaN(date.getTime()) ? value : date.toLocaleString(audience.locale === 'en' ? 'en' : 'zh-CN', { hour12: false }) }

function storedReadIds() {
  void readRevision.value
  try { return new Set(readStoredBroadcastIds(localStorage, readStorageKey.value).map((entry) => entry.id)) } catch { return new Set<string>() }
}

const unreadCount = computed(() => {
  const readIds = storedReadIds()
  return messages.value.filter((message) => !readIds.has(message.id)).length
})
const tickerMessage = computed<BroadcastMessage | null>(() => {
  const latest = messages.value.find((message) => message.level === 'normal' || message.level === 'warning') || null
  return latest && !storedReadIds().has(latest.id) ? latest : null
})
const panelStatus = computed<BroadcastPanelStatus>(() => ({
  unreadCount: unreadCount.value,
  ticker: tickerMessage.value ? { id: tickerMessage.value.id, level: tickerMessage.value.level === 'warning' ? 'warning' : 'normal', text: `${tickerMessage.value.title}：${tickerMessage.value.body}` } : null,
}))

function markRead(id: string) {
  if (!id || storedReadIds().has(id)) return
  try { rememberStoredBroadcastId(localStorage, readStorageKey.value, id) } catch { /* Keep the current page usable when storage is unavailable. */ }
  readRevision.value += 1
}

function markVisibleBroadcastsRead() {
  messages.value.forEach((message) => markRead(message.id))
}

function markTickerRead(id: string) {
  if (tickerMessage.value?.id === id) markRead(id)
}

function unwrap(value: unknown): unknown {
  if (value && typeof value === 'object' && 'code' in value) {
    const payload = value as Record<string, unknown>
    if (Number(payload.code) !== 200) throw new Error(String(payload.msg || '公告接口请求失败'))
    return payload.data
  }
  return value
}

function readDismissedEntries() {
  try { return readStoredBroadcastIds(localStorage, emergencyStorageKey.value) } catch { return [] }
}

function isEmergencyDismissed(id: string) {
  return readDismissedEntries().some((item) => item.id === id)
}

function rememberEmergencyDismissed(id: string) {
  try { rememberStoredBroadcastId(localStorage, emergencyStorageKey.value, id) } catch { /* The alert remains dismissed for this page. */ }
}

function showNextEmergency() {
  if (emergencyMessage.value) return
  let message = emergencyQueue.value.shift() || null
  while (message && (isEmergencyDismissed(message.id) || !messages.value.some((item) => item.id === message?.id))) message = emergencyQueue.value.shift() || null
  emergencyMessage.value = message
}

function syncEmergencyCandidates() {
  const urgent = messages.value.filter((message) => message.level === 'urgent')
  if (!emergencySessionInitialized) {
    emergencySessionInitialized = true
    urgent.forEach((message) => knownEmergencyIds.add(message.id))
    const latest = urgent[0]
    if (latest && !isEmergencyDismissed(latest.id)) emergencyQueue.value = [latest]
  } else {
    const fresh = urgent.filter((message) => !knownEmergencyIds.has(message.id))
      .sort((left, right) => left.seq - right.seq || Date.parse(left.time) - Date.parse(right.time))
    fresh.forEach((message) => knownEmergencyIds.add(message.id))
    emergencyQueue.value = [...emergencyQueue.value, ...fresh.filter((message) => !isEmergencyDismissed(message.id))]
      .filter((message, index, rows) => rows.findIndex((item) => item.id === message.id) === index)
  }
  const validIds = new Set(urgent.map((message) => message.id))
  emergencyQueue.value = emergencyQueue.value.filter((message) => validIds.has(message.id))
  if (emergencyMessage.value && !validIds.has(emergencyMessage.value.id)) emergencyMessage.value = null
  showNextEmergency()
}

function dismissEmergency() {
  const id = emergencyMessage.value?.id || ''
  emergencyMessage.value = null
  if (id) {
    markRead(id)
    rememberEmergencyDismissed(id)
  }
  void nextTick(() => emergencyReturnFocus?.focus())
  showNextEmergency()
}

function normalize(value: unknown): BroadcastMessage | null {
  if (!value || typeof value !== 'object') return null
  const row = value as Record<string, unknown>
  const channel = String(row.channel || row.targetChannel || '')
  if (channel && !['audience', 'public_broadcast'].includes(channel)) return null
  const id = String(row.id || row.messageId || row.broadcastId || row.seq || '')
  const body = String(row.content || row.body || row.message || '').trim()
  const title = String(row.title || '实时公告')
  const rawLevel = String(row.level || row.type || 'normal').toLowerCase()
  const level: BroadcastMessage['level'] = rawLevel === 'urgent' ? 'urgent' : rawLevel === 'warning' ? 'warning' : 'normal'
  const targetRoles = (Array.isArray(row.targetRoles) ? row.targetRoles : []).map((item) => String(item).toLowerCase())
  const expiresAt = String(row.expiresAt || '')
  if (!body || targetRoles.length > 0 || (expiresAt && Date.parse(expiresAt) <= Date.now())) return null
  const images = (Array.isArray(row.images) ? row.images : []).map((value) => {
    const image = value && typeof value === 'object' ? value as Record<string, unknown> : {}
    let url = ''
    try { url = assertSafeArtifactUrl(String(image.url || '').trim()).href } catch { /* Drop unregistered origins. */ }
    return { url, width: Number(image.width || 0), height: Number(image.height || 0), sortOrder: Number(image.sortOrder || 0) }
  }).filter((image) => image.url).sort((left, right) => left.sortOrder - right.sortOrder).slice(0, 3)
  const seq = Number(row.seq || row.sequence || 0)
  return { id: id || `${title}:${body}`, seq: Number.isSafeInteger(seq) && seq >= 0 ? seq : 0, level, title, body, time: String(row.publishTime || row.publishedAt || row.createTime || ''), expiresAt, images }
}

function replaceRows(value: unknown, requestSeq: number) {
  const payload = unwrap(value)
  if (!Array.isArray(payload)) return
  const normalizedRows = payload.map(normalize).filter((item): item is BroadcastMessage => Boolean(item))
  messages.value = reconcileBroadcastHistory(messages.value, normalizedRows, requestSeq, revokedSeqById)
  const byId = new Map(messages.value.map((message) => [message.id, message]))
  if (activeMessage.value) activeMessage.value = byId.get(activeMessage.value.id) || null
  if (previewImage.value) {
    const current = byId.get(previewImage.value.messageId)
    const image = current?.images.find((item) => item.url === previewImage.value?.image.url)
    previewImage.value = current && image ? { messageId: current.id, image } : null
  }
  emergencyQueue.value = emergencyQueue.value.map((message) => byId.get(message.id)).filter((message): message is BroadcastMessage => Boolean(message))
  if (emergencyMessage.value) emergencyMessage.value = byId.get(emergencyMessage.value.id) || null
  lastSeq = Math.max(lastSeq, ...messages.value.map((item) => item.seq), 0)
  syncEmergencyCandidates()
}

async function loadHistory() {
  if (!eventId.value) return
  const requestSeq = lastSeq
  try {
    const query = new URLSearchParams({ eventId: eventId.value, channel: 'audience', limit: '100' })
    const response = await fetch(`${apiBase}/venue/h5/broadcast/history?${query}`, { cache: 'no-store', headers: { Accept: 'application/json' } })
    if (!response.ok) throw new Error(response.status === 403 ? '无权访问该频道' : '公告记录加载失败')
    replaceRows(await response.json(), requestSeq)
    error.value = ''
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '公告记录加载失败'
  }
}

function startPolling() {
  if (!pollTimer) pollTimer = window.setInterval(() => void loadHistory(), 30_000)
}

function mergeRealtime(item: BroadcastMessage) {
  const revokedSeq = revokedSeqById.get(item.id)
  if (revokedSeq !== undefined && item.seq <= revokedSeq) return
  if (revokedSeq !== undefined) revokedSeqById.delete(item.id)
  messages.value = [item, ...messages.value.filter((row) => row.id !== item.id)]
    .sort((left, right) => right.seq - left.seq || Date.parse(right.time) - Date.parse(left.time)).slice(0, 100)
  lastSeq = Math.max(lastSeq, item.seq)
  syncEmergencyCandidates()
}

function revokeMessage(id: string, seq: number) {
  if (!id) return
  revokedSeqById.set(id, Math.max(seq, revokedSeqById.get(id) || 0))
  const revoked = revokeBroadcastState({
    messages: messages.value,
    activeMessage: activeMessage.value,
    emergencyMessage: emergencyMessage.value,
    emergencyQueue: emergencyQueue.value,
    previewMessageId: previewImage.value?.messageId || '',
  }, id)
  messages.value = revoked.messages
  activeMessage.value = revoked.activeMessage
  emergencyMessage.value = revoked.emergencyMessage
  emergencyQueue.value = revoked.emergencyQueue
  if (!revoked.previewMessageId) previewImage.value = null
  lastSeq = Math.max(lastSeq, seq)
  showNextEmergency()
}

function connect() {
  if (!eventId.value) return
  if (typeof EventSource === 'undefined') { startPolling(); return }
  source?.close()
  const clientId = typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}`
  const query = new URLSearchParams({ eventId: eventId.value, channel: 'audience', clientId, ...(lastSeq ? { lastEventId: String(lastSeq) } : {}) })
  source = new EventSource(`${apiBase}/venue/sse/broadcast?${query}`)
  source.addEventListener('broadcast', (event) => {
    try {
      const item = normalize(JSON.parse((event as MessageEvent<string>).data))
      if (!item) return
      const eventSeq = Number((event as MessageEvent<string>).lastEventId || item.seq)
      if (Number.isSafeInteger(eventSeq) && eventSeq > item.seq) item.seq = eventSeq
      mergeRealtime(item)
    } catch { /* Ignore malformed events. */ }
  })
  source.addEventListener('broadcast-revoke', (event) => {
    try {
      const payload = JSON.parse((event as MessageEvent<string>).data) as Record<string, unknown>
      const id = String(payload.id || payload.messageId || payload.broadcastId || '')
      const seq = Number((event as MessageEvent<string>).lastEventId || payload.seq || lastSeq)
      revokeMessage(id, Number.isSafeInteger(seq) ? seq : lastSeq)
    } catch { void loadHistory() }
  })
  source.onerror = startPolling
  source.onopen = () => { if (pollTimer) window.clearInterval(pollTimer); pollTimer = 0; void loadHistory() }
}

function handleVisibilityChange() {
  if (document.visibilityState === 'hidden') { source?.close(); source = null; return }
  void loadHistory().finally(connect)
}

function resetBroadcastSession() {
  source?.close()
  source = null
  if (pollTimer) window.clearInterval(pollTimer)
  pollTimer = 0
  lastSeq = 0
  emergencySessionInitialized = false
  knownEmergencyIds.clear()
  revokedSeqById.clear()
  messages.value = []
  activeMessage.value = null
  emergencyMessage.value = null
  emergencyQueue.value = []
  previewImage.value = null
  error.value = ''
}

function openDetail(message: BroadcastMessage) {
  detailReturnFocus = document.activeElement as HTMLElement | null
  activeMessage.value = message
}

function closeDetail() {
  activeMessage.value = null
  void nextTick(() => detailReturnFocus?.focus())
}

function openPreview(message: BroadcastMessage, image: BroadcastImage) {
  previewImage.value = { messageId: message.id, image }
}

function requestPanelClose() {
  emit('close')
}

function trapFocus(event: KeyboardEvent, root: HTMLElement | null) {
  if (event.key !== 'Tab' || !root) return
  const controls = [...root.querySelectorAll<HTMLElement>('button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')]
    .filter((element) => !element.hasAttribute('hidden'))
  const first = controls[0]
  const last = controls[controls.length - 1]
  if (!first || !last) { event.preventDefault(); root.focus(); return }
  if (event.shiftKey && (document.activeElement === first || !root.contains(document.activeElement))) { event.preventDefault(); last.focus() }
  else if (!event.shiftKey && (document.activeElement === last || !root.contains(document.activeElement))) { event.preventDefault(); first.focus() }
}

function handleKeydown(event: KeyboardEvent) {
  if (previewImage.value) return
  if (emergencyMessage.value) {
    if (event.key === 'Escape') event.preventDefault()
    trapFocus(event, emergency.value)
    return
  }
  if (activeMessage.value) {
    if (event.key === 'Escape') { event.preventDefault(); closeDetail(); return }
    trapFocus(event, detail.value)
    return
  }
  if (!props.open) return
  if (event.key === 'Escape') { event.preventDefault(); requestPanelClose(); return }
  trapFocus(event, panel.value)
}

watch(panelStatus, (status) => emit('status', status), { immediate: true })
watch(eventId, (current, previous) => {
  if (current === previous) return
  resetBroadcastSession()
  if (mounted && current) void loadHistory().finally(connect)
})
watch(() => props.open, (open, wasOpen) => {
  if (open) {
    if (!wasOpen) panelReturnFocus = document.activeElement as HTMLElement | null
    markVisibleBroadcastsRead()
    if (mounted) void loadHistory().then(() => markVisibleBroadcastsRead())
    void nextTick(() => panelClose.value?.focus())
  } else if (wasOpen) {
    void nextTick(() => panelReturnFocus?.focus())
  }
}, { immediate: true })
watch(activeMessage, (current, previous) => {
  if (current) void nextTick(() => detailClose.value?.focus())
  else if (previous) void nextTick(() => detailReturnFocus?.focus())
})
watch(emergencyMessage, (current, previous) => {
  if (current) {
    if (!previous) emergencyReturnFocus = document.activeElement as HTMLElement | null
    void nextTick(() => emergencyDismiss.value?.focus())
  } else if (previous) void nextTick(() => emergencyReturnFocus?.focus())
})
watch([() => props.open, activeMessage, emergencyMessage, previewImage], ([open, detailMessage, emergencyAlert, preview]) => { document.documentElement.classList.toggle('vf-broadcast-modal-open', Boolean(open || detailMessage || emergencyAlert || preview)) })

defineExpose({ markTickerRead, markVisibleBroadcastsRead })
onMounted(() => { mounted = true; void loadHistory().then(() => { if (props.open) markVisibleBroadcastsRead() }).finally(connect); document.addEventListener('visibilitychange', handleVisibilityChange); window.addEventListener('keydown', handleKeydown) })
onBeforeUnmount(() => { mounted = false; source?.close(); if (pollTimer) window.clearInterval(pollTimer); document.removeEventListener('visibilitychange', handleVisibilityChange); window.removeEventListener('keydown', handleKeydown); document.documentElement.classList.remove('vf-broadcast-modal-open') })
</script>

<template>
  <div class="vf-broadcast-modal" :class="{ 'is-open': open }" :aria-hidden="!open" @click.self="requestPanelClose">
    <section ref="panel" class="vf-broadcast-sheet" role="dialog" aria-modal="true" :aria-label="t('公告记录')" tabindex="-1">
      <header class="vf-broadcast-head"><div><span>{{ t('实时公告') }}</span><strong>{{ t('公告记录') }}</strong></div><button ref="panelClose" class="vf-broadcast-close" type="button" :aria-label="t('关闭公告记录')" @click="requestPanelClose">×</button></header>
      <div class="vf-broadcast-legend" role="note" :aria-label="t('公告颜色说明')"><span class="vf-broadcast-legend-title">{{ t('颜色说明') }}</span><div class="vf-broadcast-legend-items"><span class="vf-broadcast-legend-item normal"><i />{{ t('一般信息') }}</span><span class="vf-broadcast-legend-item warning"><i />{{ t('重要提醒') }}</span><span class="vf-broadcast-legend-item urgent"><i />{{ t('紧急公告') }}</span></div></div>
      <div class="vf-broadcast-history"><article v-for="message in messages" :key="message.id" class="vf-broadcast-item" :class="message.level" data-i18n-skip><header><strong>{{ message.title }}</strong><time>{{ formatTime(message.time) }}</time></header><p>{{ message.body }}</p><span v-if="message.images.length" class="vf-broadcast-image-count">{{ message.images.length }} {{ audience.locale === 'en' ? (message.images.length === 1 ? 'image' : 'images') : '张图片' }}</span><button class="vf-broadcast-detail-trigger" type="button" @click="openDetail(message)">{{ t('查看详情') }}</button></article><div v-if="!messages.length" class="vf-broadcast-empty">{{ error || t('当前活动暂无公告') }}</div></div>
    </section>
  </div>
  <div v-if="activeMessage" class="vf-broadcast-detail-modal is-open" @click.self="closeDetail"><section ref="detail" class="vf-broadcast-detail-card" role="dialog" aria-modal="true" :aria-label="t('广播详情')" tabindex="-1"><header class="vf-broadcast-detail-head"><h2>{{ activeMessage.title }}</h2><button ref="detailClose" class="vf-broadcast-detail-action" type="button" @click="closeDetail">{{ t('关闭') }}</button></header><time class="vf-broadcast-item-time">{{ formatTime(activeMessage.time) }}</time><div class="vf-broadcast-detail-body">{{ activeMessage.body }}</div><div class="vf-broadcast-detail-gallery"><button v-for="image in activeMessage.images" :key="image.url" class="vf-broadcast-image-button" type="button" :aria-label="t('点击查看大图')" @click="openPreview(activeMessage, image)"><img :src="image.url" :alt="t('广播图片')" loading="lazy" /></button></div></section></div>
  <div v-if="emergencyMessage" class="vf-emergency-modal is-open"><section ref="emergency" class="vf-emergency-card" role="alertdialog" aria-modal="true" aria-labelledby="vfEmergencyTitle" aria-describedby="vfEmergencyBody" tabindex="-1"><div class="vf-emergency-kicker">{{ t('紧急公告') }}</div><h2 id="vfEmergencyTitle" class="vf-emergency-title">{{ emergencyMessage.title }}</h2><time class="vf-emergency-time">{{ audience.locale === 'en' ? 'Published: ' : '发布时间：' }}{{ formatTime(emergencyMessage.time) }}</time><div id="vfEmergencyBody" class="vf-emergency-body">{{ emergencyMessage.body }}</div><div class="vf-emergency-gallery"><button v-for="image in emergencyMessage.images" :key="image.url" class="vf-broadcast-image-button" type="button" :aria-label="t('点击查看大图')" @click="openPreview(emergencyMessage, image)"><img :src="image.url" :alt="t('广播图片')" loading="lazy" /></button></div><button ref="emergencyDismiss" class="vf-emergency-dismiss" type="button" @click="dismissEmergency">{{ t('我已知晓，关闭提醒') }}</button></section></div>
  <VfImagePreview v-if="previewImage" :src="previewImage.image.url" :width="previewImage.image.width" :height="previewImage.image.height" :alt="t('广播图片')" @close="previewImage = null" />
</template>
